import { Box } from '@mui/material'
import { DataGridPro, GridSortModel, GridFilterModel, GridRenderHeaderFilterProps } from '@mui/x-data-grid-pro'
import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'

import { FeedbackGridApi } from './api/feedbackGridApi'
import { CategoryEditDialog } from './components/CategoryEditDialog'
import { FeedbackGridToolbar } from './components/FeedbackGridToolbar'
import { MultiSelectHeaderFilter } from './components/filters/MultiSelectHeaderFilter'
import { TextHeaderFilter } from './components/filters/TextHeaderFilter'
import { FeedbackGridProvider } from './context/FeedbackGridContext'
import { useFeedbackData } from './hooks/useFeedbackData'
import { useGridColumns } from './hooks/useGridColumns'
import { useGridExport } from './hooks/useGridExport'
import { useGridFilters } from './hooks/useGridFilters'
import { FeedbackGridProps, FeedbackRow, FilterValue } from './types/feedbackGrid.types'
import { buildMermaidDefinition, type CategoryData } from './utils/mermaidBuilder'

const TextFilter = (params: GridRenderHeaderFilterProps) => <TextHeaderFilter {...params} />
const MultiSelectFilter = (params: GridRenderHeaderFilterProps) => <MultiSelectHeaderFilter {...params} />

export const FeedbackGrid: React.FC<FeedbackGridProps> = ({
	endpoints,
	initialData,
	columns,
	systemColumns,
	settings,
	blockId,
	isUserCategoryManager,
	initialFilters = {},
	translations = {},
	defaultSort = null,
}) => {
	const api = useMemo(() => new FeedbackGridApi(endpoints), [endpoints])

	const {
		filterModel,
		handleFilterModelChange,
		internalFilterModelRef,
		getFiltersForBackend,
		applyFiltersToBackend,
		filterOptions,
		loadingOptions,
		loadFilterOptions,
		initialFilterModel,
	} = useGridFilters({
		api,
		columns,
		systemColumns,
		initialFilters,
	})

	const { rows, setRows, loading, setLoading, error, rowCount, page, setPage, sortModel, setSortModel, fetchData } =
		useFeedbackData({
			api,
			initialData,
			itemsPerPage: settings.itemsPerPage,
			defaultSort,
		})

	const { exporting, exportData } = useGridExport(api)

	// State for category edit dialog
	const [categoryEditOpen, setCategoryEditOpen] = useState(false)
	const [categoryEditFeedbackId, setCategoryEditFeedbackId] = useState<number | null>(null)

	const prevFiltersRef = useRef<FilterValue>(getFiltersForBackend(initialFilterModel))
	const initializedRef = useRef(false)

	// Combined data fetching and filter synchronization effect
	useEffect(() => {
		const syncFiltersAndFetch = async () => {
			if (!initializedRef.current) {
				initializedRef.current = true
				if (rows.length === 0) {
					fetchData()
				}
				return
			}

			const currentFilters = getFiltersForBackend(internalFilterModelRef.current)
			const filtersChanged = JSON.stringify(currentFilters) !== JSON.stringify(prevFiltersRef.current)

			if (filtersChanged) {
				prevFiltersRef.current = currentFilters
				setLoading(true)
				await applyFiltersToBackend(currentFilters)
				setPage(0)
				// If page was already 0, setPage(0) won't trigger the [page, sortModel] effect
				if (page === 0) {
					fetchData()
				}
			}
		}

		const timer = setTimeout(syncFiltersAndFetch, initializedRef.current ? 500 : 0)
		return () => clearTimeout(timer)
	}, [filterModel, applyFiltersToBackend, fetchData, getFiltersForBackend, page])

	// Refetch when page or sort changes (except when handled by filter change)
	useEffect(() => {
		if (!initializedRef.current) {
			return
		}
		fetchData()
	}, [page, sortModel, fetchData])

	const handleSortModelChange = useCallback(
		(newSortModel: GridSortModel) => {
			setSortModel(newSortModel)
		},
		[setSortModel],
	)

	const handleClearFilters = useCallback(() => {
		handleFilterModelChange({ items: [] })
	}, [handleFilterModelChange])

	const handleRowClick = useCallback((params: any) => {
		// Open feedback in new tab using the link from backend
		if (params.row.feedbackLink) {
			window.open(params.row.feedbackLink, '_blank')
		}
	}, [])

	const handleEditCategories = useCallback((feedbackId: number) => {
		setCategoryEditFeedbackId(feedbackId)
		setCategoryEditOpen(true)
	}, [])

	const gridColumns = useGridColumns({
		columns,
		systemColumns,
		widthUnit: settings.widthUnit,
		translations,
		filterOptions,
		api,
		onEditCategories: handleEditCategories,
		isUserCategoryManager,
		TextFilter,
		MultiSelectFilter,
	})

	return (
		<FeedbackGridProvider value={{ translations, filterOptions, loadingOptions, loadFilterOptions }}>
			<Box
				sx={{
					height: '100%',
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					'& .MuiDataGrid-toolbarContainer': {
						borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
					},
				}}
			>
				<FeedbackGridToolbar
					filterModel={filterModel}
					onClearFilters={handleClearFilters}
					onExport={exportData}
					exporting={exporting}
					translations={translations}
				/>
				<Box sx={{ flexGrow: 1, minHeight: 0 }}>
					<DataGridPro
						rows={rows}
						columns={gridColumns}
						loading={loading}
						sortModel={sortModel}
						onSortModelChange={handleSortModelChange}
						filterModel={filterModel}
						onFilterModelChange={handleFilterModelChange}
						filterMode="server"
						sortingMode="server"
						headerFilters
						onRowClick={handleRowClick}
						disableRowSelectionOnClick
						isRowSelectable={() => false}
						getRowHeight={() => 'auto'}
						disableVirtualization={true}
						pagination
						paginationMode="server"
						rowCount={rowCount}
						paginationModel={{
							page: page,
							pageSize: settings.itemsPerPage,
						}}
						onPaginationModelChange={(model) => {
							setPage(model.page)
						}}
						pageSizeOptions={[settings.itemsPerPage]}
						getRowClassName={(params) => {
							return params.id === categoryEditFeedbackId ? 'row-editing' : ''
						}}
						sx={{
							height: '100%',
							width: '100%',
							'& .MuiDataGrid-headerFilterRow': {
								backgroundColor: 'rgba(0, 0, 0, 0.02)',
								'& .MuiDataGrid-columnHeader': {
									pt: 1,
									pb: 1,
									display: 'flex',
									alignItems: 'center',
									minHeight: '64px !important',
									maxHeight: 'none !important',
									height: 'auto !important',
									'&:focus': {
										outline: 'none',
									},
									'&:focus-within': {
										outline: 'none',
									},
								},
							},
							'& .MuiDataGrid-row': {
								cursor: 'pointer',
								minHeight: 'auto !important',
								maxHeight: 'none !important',
								'&.row-editing': {
									backgroundColor: 'rgba(0, 157, 157, 0.08) !important',
								},
								'&.Mui-selected': {
									backgroundColor: 'transparent',
									'&:hover': {
										backgroundColor: 'rgba(0, 0, 0, 0.04)',
									},
								},
							},
							'& .MuiDataGrid-cell:focus': {
								outline: 'none',
							},
							'& .MuiDataGrid-cell:focus-within': {
								outline: 'none',
							},
							'& .MuiDataGrid-columnHeader:focus': {
								outline: 'none',
							},
							'& .MuiDataGrid-columnHeader:focus-within': {
								outline: 'none',
							},
							'& .MuiDataGrid-row [role="gridcell"]': {
								whiteSpace: 'normal !important',
								wordWrap: 'break-word !important',
								lineHeight: '1.5',
								display: 'flex',
								alignItems: 'flex-start',
								py: 1,
								minHeight: 'auto !important',
								maxHeight: 'none !important',
							},
						}}
						getRowId={(row: FeedbackRow) => row.id}
					/>
				</Box>

				{error && <Box sx={{ p: 2, color: 'error.main', textAlign: 'center' }}>Error: {error}</Box>}

				{/* Category Edit Dialog */}
				{categoryEditFeedbackId !== null && (
					<CategoryEditDialog
						feedbackId={categoryEditFeedbackId}
						open={categoryEditOpen}
						onClose={() => {
							setCategoryEditOpen(false)
							setCategoryEditFeedbackId(null)
						}}
						onSave={(categoryData, updatedValues) => {
							setCategoryEditOpen(false)
							setCategoryEditFeedbackId(null)

							// Update local row data with new mermaid diagram and other values
							setRows((prevRows) => {
								return prevRows.map((row) => {
									if (row.id === categoryEditFeedbackId) {
										let newRow = { ...row }

										// Apply updated values from backend if available
										if (updatedValues) {
											newRow = {
												...newRow,
												annotated: updatedValues.annotated,
												checked: updatedValues.checked,
												ta_sentiment: updatedValues.sentiment,
												ta_sentiment_color: updatedValues.sentiment_color || '#A7A7A7',
												ta_sentiment_label: updatedValues.sentiment_label || translations.neutral || 'Neutral',
											}
										} else {
											newRow.annotated = true
										}

										// Build new mermaid definition from category data
										if (categoryData && categoryData.size > 0) {
											const allCategories: CategoryData[] = []
											categoryData.forEach((questionCategories) => {
												allCategories.push(...questionCategories)
											})

											// Remove duplicates based on category ID
											const uniqueCategories = allCategories.filter(
												(cat, index, self) => self.findIndex((c) => c.id === cat.id) === index,
											)

											const newGraphDefinition = buildMermaidDefinition(uniqueCategories)
											newRow.ta_categories_data = {
												...newRow.ta_categories_data,
												graphDefinition: newGraphDefinition,
												config: newRow.ta_categories_data?.config || {
													theme: 'base',
												},
												categories: uniqueCategories.map((cat) => ({
													id: cat.id,
													name: cat.name,
													sentiment: cat.sentiment,
												})),
											}
										} else if (categoryData && categoryData.size === 0) {
											// If categoryData is explicitly empty (all categories removed)
											newRow.ta_categories_data = null
										}

										return newRow
									}
									return row
								})
							})
						}}
						api={api}
						translations={translations}
					/>
				)}
			</Box>
		</FeedbackGridProvider>
	)
}
